import "./CaptionPublishingLanding.scss";
import CaptionPricingSection from "./CaptionPricingSection";

const REVIEWS = [
  {
    name: "José David Herazo",
    photo: "/images/caption-reviews/review-1.svg",
    text: "Really useful and accurate. Time stamps are really helpful. Also affordable.",
  },
  {
    name: "Rhonda Burgess Howse",
    photo: "/images/caption-reviews/review-2.svg",
    text: "So easy to use. Very accurate transcription. Highly pleased with my purchase.",
  },
  {
    name: "Tanja",
    photo: "/images/caption-reviews/review-3.svg",
    text: "Great tool! Translation from speech to text is fast and accurate. It saves me lots of time in the video edit",
  },
  {
    name: "Dan S",
    photo: "/images/caption-reviews/review-4.svg",
    text: "Powerful, out of the box transcriber for V-Blogs that intuitive and super easy to get started and delivering instant 99% accurate results.",
  },
  {
    name: "Cora Carmody",
    photo: "/images/caption-reviews/review-5.svg",
    text: "Quick, easy, superior quality, reasonable pricing.",
  },
  {
    name: "Eric Van Den Hoek",
    photo: "/images/caption-reviews/review-6.svg",
    text: "Way better than hours and hours of typing and translating your recordings",
  },
  {
    name: "Co_2020",
    photo: "/images/caption-reviews/review-7.svg",
    text: "It's an awesome tool to save some time!",
  },
  {
    name: "Jeroen Thibaut",
    photo: "/images/caption-reviews/review-8.svg",
    text: "Great software with clean interface. Saves me so much time I wish I found it earlier.",
  },
  {
    name: "Lavinia",
    photo: "/images/caption-reviews/review-9.svg",
    text: "Digipank Caption Publishing is a nice and easy tool to transcribe your interview! I really recommend it.",
  },
];

const FEATURES = [
  {
    icon: "⏱",
    title: "Timed segments",
    text: "Subtitles split into readable chunks with start and end timestamps.",
  },
  {
    icon: "⇩",
    title: "SRT & VTT export",
    text: "Download YouTube-ready .srt or .vtt files in one click.",
  },
  {
    icon: "Aa",
    title: "Phonetic script",
    text: "Latin transliteration (Hinglish) for audiences who read English letters.",
  },
  {
    icon: "अ",
    title: "Native script",
    text: "Devanagari output aligned to how words sound in the video language.",
  },
  {
    icon: "▶",
    title: "Preview first",
    text: "Watch your upload before generating captions so you know what will be processed.",
  },
  {
    icon: "☁",
    title: "Secure upload",
    text: "Large files use encrypted storage upload — same flow you already use on the tool.",
  },
  {
    icon: "↻",
    title: "Upload history",
    text: "Reopen past jobs, replay transcripts, and start a new upload anytime.",
  },
  {
    icon: "✓",
    title: "Free plan included",
    text: "3 hours total processing, 20 minutes per video, up to 100 MB per file.",
  },
];

const FAQ_ITEMS = [
  {
    question: "How does Caption Publishing work?",
    answer:
      "Sign in with Google, upload a video, preview it, then generate subtitles. You receive a phonetic (Latin) script and a native-script version with matching timings. Download .srt or .vtt for YouTube or any platform.",
  },
  {
    question: "Which video formats are supported?",
    answer:
      "MP4, WebM, MOV, AVI, and MKV. Free plan limits: 100 MB per file, 20 minutes per video, and 3 hours of total processed time per account.",
  },
  {
    question: "What is the difference between phonetic and native script?",
    answer:
      "Phonetic script uses the English alphabet (e.g. Hinglish). Native script uses Devanagari for Hindi-sounding speech in the video. Both share the same timing segments.",
  },
  {
    question: "Which subtitle formats can I download?",
    answer:
      "SubRip (.srt) and WebVTT (.vtt). Both are widely supported by YouTube, Vimeo, and most editors.",
  },
  {
    question: "Are my files secure?",
    answer:
      "Uploads are tied to your Google account. Larger files are sent through a secure presigned upload flow. Only you (and Digipank admins for support) can access your job history.",
  },
];

const CaptionPublishingLanding = ({
  showPricing,
  isLoggedIn,
  onFreePlanClick,
  onGetStartedPlan,
  onTalkToSales,
}) => (
  <div className="cp-landing">
    <section className="cp-landing__trust" aria-label="Trust indicators">
      <p className="cp-landing__trust-label">
        Built for creators publishing to YouTube and multilingual audiences
      </p>
      <ul className="cp-landing__trust-tags">
        <li>YouTube-ready</li>
        <li>Hinglish + Devanagari</li>
        <li>Google Sign-In</li>
        <li>Free tier available</li>
      </ul>
    </section>

    <section className="cp-landing__services">
      <div className="cp-landing__section-head">
        <h2 className="cp-landing__heading">Two scripts, one workflow</h2>
        <p className="cp-landing__lede">
          Every upload produces both transliteration and native-script captions — no extra steps.
        </p>
      </div>

      <div className="cp-landing__service-grid">
        <article className="cp-landing__service-card">
          <div className="cp-landing__service-icon cp-landing__service-icon--latin" aria-hidden="true">
            Aa
          </div>
          <h3>Phonetic script</h3>
          <p>
            Latin transliteration for global viewers — readable with the English alphabet. Example:
            &ldquo;Kaha ho aap.&rdquo;
          </p>
          <a className="cp-landing__text-link" href="#caption-tool">
            Try in the tool →
          </a>
        </article>

        <article className="cp-landing__service-card">
          <div className="cp-landing__service-icon cp-landing__service-icon--native" aria-hidden="true">
            अ
          </div>
          <h3>Video language script</h3>
          <p>
            Devanagari phonetic typing matched to speech in your video — same timings as the Latin
            track.
          </p>
          <a className="cp-landing__text-link" href="#caption-tool">
            Try in the tool →
          </a>
        </article>
      </div>
    </section>

    <section className="cp-landing__feature-row">
      <div className="cp-landing__feature-copy">
        <span className="cp-landing__eyebrow">Readable subtitles</span>
        <h2 className="cp-landing__heading">Naturally segmented, timed for playback</h2>
        <p>
          Segments follow speech rhythm so captions stay readable on mobile and TV. Timestamps are
          included for every line in both script views.
        </p>
      </div>
      <div className="cp-landing__feature-visual" aria-hidden="true">
        <div className="cp-landing__mock-editor">
          <div className="cp-landing__mock-cue">
            <span className="cp-landing__mock-time">0:04 – 0:07</span>
            <span className="cp-landing__mock-text">Namaste, aaj hum seekhenge...</span>
          </div>
          <div className="cp-landing__mock-cue cp-landing__mock-cue--alt">
            <span className="cp-landing__mock-time">0:07 – 0:11</span>
            <span className="cp-landing__mock-text">Namaste, aaj hum seekhenge...</span>
          </div>
          <div className="cp-landing__mock-wave">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    </section>

    <section className="cp-landing__feature-row cp-landing__feature-row--reverse">
      <div className="cp-landing__feature-copy">
        <span className="cp-landing__eyebrow">Export formats</span>
        <h2 className="cp-landing__heading">YouTube-ready .srt and .vtt</h2>
        <p>
          Download from section 3 and 4 in the tool. Pick SubRip or WebVTT — buttons sit beside each
          script block so you always export the right language track.
        </p>
      </div>
      <div className="cp-landing__feature-visual" aria-hidden="true">
        <div className="cp-landing__mock-formats">
          <button type="button" className="cp-landing__mock-pill cp-landing__mock-pill--active">
            .SRT
          </button>
          <button type="button" className="cp-landing__mock-pill">
            .VTT
          </button>
          <div className="cp-landing__mock-file">
            <span className="cp-landing__mock-file-name">my-video.srt</span>
            <span className="cp-landing__mock-badge">Ready</span>
          </div>
        </div>
      </div>
    </section>

    <section className="cp-landing__features-grid-section">
      <div className="cp-landing__section-head">
        <h2 className="cp-landing__heading">Everything in the Caption Publishing tool</h2>
        <p className="cp-landing__lede">
          Same four-step flow you use today — upload, preview, generate, download — with a cleaner
          presentation.
        </p>
      </div>

      <ul className="cp-landing__features-grid">
        {FEATURES.map((feature) => (
          <li key={feature.title} className="cp-landing__feature-item">
            <span className="cp-landing__feature-icon" aria-hidden="true">
              {feature.icon}
            </span>
            <h3>{feature.title}</h3>
            <p>{feature.text}</p>
          </li>
        ))}
      </ul>

      <div className="cp-landing__cta-row">
        <a className="cp-landing__btn cp-landing__btn--primary" href="#caption-tool">
          Open caption tool
        </a>
        <button
          type="button"
          className="cp-landing__btn cp-landing__btn--ghost"
          onClick={onTalkToSales}
        >
          Talk to sales
        </button>
      </div>
    </section>

    {showPricing && (
      <CaptionPricingSection
        isLoggedIn={isLoggedIn}
        onFreePlanClick={onFreePlanClick}
        onGetStarted={onGetStartedPlan}
      />
    )}

    <section className="cp-landing__faq" aria-labelledby="cp-faq-heading">
      <h2 id="cp-faq-heading" className="cp-landing__faq-title">
        FAQ
      </h2>
      <div className="cp-landing__faq-list">
        {FAQ_ITEMS.map((item, index) => (
          <details key={item.question} className="cp-landing__faq-item" open={index === 0}>
            <summary>{item.question}</summary>
            <p>{item.answer}</p>
          </details>
        ))}
      </div>
    </section>

    <section className="cp-landing__reviews" aria-labelledby="cp-reviews-heading">
      <span className="cp-landing__reviews-badge">
        <span className="cp-landing__reviews-badge-icon" aria-hidden="true">
          ☺
        </span>
        Customer reviews
      </span>
      <h2 id="cp-reviews-heading" className="cp-landing__reviews-title">
        Loved by Thousands of Users
      </h2>
      <p className="cp-landing__reviews-rating">
        Rated <span className="cp-landing__reviews-stars" aria-hidden="true">★★★★★</span> 4.7/5 on
        <span className="cp-landing__reviews-trustpilot">
          <span aria-hidden="true">★</span> Trustpilot
        </span>
      </p>
      <ul className="cp-landing__reviews-grid">
        {REVIEWS.map((review) => (
          <li key={review.name} className="cp-landing__review-card">
            <div className="cp-landing__review-head">
              <img
                src={review.photo}
                alt={`${review.name} profile`}
                className="cp-landing__review-photo"
                loading="lazy"
              />
              <div className="cp-landing__review-meta">
                <span className="cp-landing__review-stars" aria-hidden="true">
                  ★★★★★
                </span>
                <span className="cp-landing__review-rated">Rated by</span>
                <span className="cp-landing__review-name">{review.name.toUpperCase()}</span>
              </div>
            </div>
            <p className="cp-landing__review-text">{review.text}</p>
            <span className="cp-landing__review-quote" aria-hidden="true">
              &rdquo;
            </span>
          </li>
        ))}
      </ul>
      <p className="cp-landing__reviews-footer">
        <span className="cp-landing__reviews-heart" aria-hidden="true">
          ♥
        </span>
        Trusted by <strong>6M+</strong> users and <strong>41,000+</strong> teams around the world.
      </p>
    </section>
  </div>
);

export default CaptionPublishingLanding;
